import { policyList } from "@/enums/permissions";
import {
  DeleteCode,
  PharmacistInterface,
  Setting,
  User
} from "@/interfaces/user";
import { EventBus, store } from "@/internal";
import { authService } from "@/services/auth.service";
import { messagesService } from "@/services/messages.service";
import { permissionRoleService } from "@/services/permissions.service";
import { pharmacistService } from "@/services/pharmacist.service";
import { traceabilityService } from "@/services/traceability.service";
import { usersService } from "@/services/user.service";
import { AuthState } from "@/vuex/auth/types";
import { RootState } from "@/vuex/types";
import { AxiosResponse } from "axios";
import differenceInSeconds from "date-fns/differenceInSeconds";
import fromUnixTime from "date-fns/fromUnixTime";
import Cookie from "js-cookie";
import jwtDecode from "jwt-decode";
import every from "lodash/every";
import omit from "lodash/omit";
import Vue from "vue";
import { ActionContext, ActionTree } from "vuex";
import {
  formParsePharmacist,
  formParseUser,
  setPharmacistLocations
} from "../modules/user/user.actions";

type AuthActionContext = ActionContext<AuthState, RootState>;
type AuthActionTree = ActionTree<AuthState, RootState>;

export const actions: AuthActionTree = {
  async getByToken(context: AuthActionContext): Promise<any> {
    try {
      const requestData = {
        method: "GET",
        url: `${process.env.VUE_APP_BASE_URL}/users/get_by_token`
      };
      const response: AxiosResponse = await Vue.axios(requestData);
      const userData = response.data.data;
      context.commit("setUser", userData);

      const userConfigurations = await Promise.all([
        // Services for this account
        permissionRoleService.servicecByAccount(
          userData.settings!.current_account_id
        ),
        // Policies by roles
        permissionRoleService.policiesByUser(userData.id)
      ]);
      context.commit("setServices", userConfigurations[0].services);
      context.commit("setUserPermissions", userConfigurations[1]);
      const hasPermission = context.getters.hasPermission;
      // Only if user has policy for altering locations or users
      if (
        hasPermission(policyList.viewUsers) ||
        hasPermission(policyList.viewLocations)
      ) {
        store.dispatch("AccountManagementModule/loadAvailability");
      }
    } catch (e) {
      messagesService.renderErrorMessage(e);
    }
  },

  async logout(context: AuthActionContext, fromIdle = false): Promise<void> {
    if (fromIdle) {
      const appMap = Cookie.get("app_map");
      const map = JSON.parse(appMap!);
      const allIdle = every(map, ["idle", true]);
      if (!allIdle) {
        return;
      }
    }
    const token = Cookie.get("auth_data");
    if (token) {
      await authService.revokeToken();
      context.commit("unsetUser");
      context.commit("setAccessToken", null);
      Cookie.remove("app_map", {
        domain: process.env.VUE_APP_COOKIE_DOMAIN
      });
      Cookie.remove("auth_data", {
        domain: `${process.env.VUE_APP_COOKIE_DOMAIN}`
      });
      Cookie.remove("userInfo");
      Cookie.remove("referrer_app", {
        domain: `${process.env.VUE_APP_COOKIE_DOMAIN}`
      });
      window.location.href = `${
        process.env.VUE_APP_OAUTH_URL
      }/logout?response_type=code&client_id=${
        process.env.VUE_APP_OAUTH_CLIENT_ID
      }&redirect_uri=${
        process.env.VUE_APP_CORE_FRONTEND
      }/oauth/callback&response_type=code&scope=${encodeURI(
        process.env.VUE_APP_OAUTH_SCOPES
      )}`;
    }
    window.location.href = `${process.env.VUE_APP_OAUTH_URL}/logout?client_id=${
      process.env.VUE_APP_OAUTH_CLIENT_ID
    }&logout_uri=${process.env.VUE_APP_CORE_FRONTEND}`;
  },

  setCurrentLocation(context: AuthActionContext, location: Location): void {
    context.commit("setCurrentLocation", location);
  },

  setAccessToken(context: AuthActionContext, token: string) {
    if (!token) {
      const authCookie = Cookie.get("auth_data");
      token = authCookie ? authCookie : "";
      // unset user if no token is present
      if (!token) {
        context.commit("unsetUser");
      }
    }

    if (token) {
      const decoded: { exp: number } = jwtDecode(token);
      Cookie.set("token_exp", String(decoded.exp), {
        domain: process.env.VUE_APP_COOKIE_DOMAIN
      });
    }

    context.commit("setAccessToken", token);
  },

  async checkToken(context: AuthActionContext): Promise<void> {
    if (!context.getters.isLogged) {
      return;
    }

    const now = new Date();
    const expiration = Cookie.get("token_exp");
    const diff =
      (expiration && differenceInSeconds(fromUnixTime(+expiration), now)) ||
      200;

    if (diff <= 80) {
      const token = await authService.refreshToken();
      if (token) {
        context.dispatch("setAccessToken", token);
      }
    }
  },

  setRedirect(context: AuthActionContext, url: string) {
    context.commit("setRedirect", url);
  },

  async updateUser(context: AuthActionContext, user: any) {
    try {
      context.commit("setLoading", true);
      const getterPharmacistInfo =
        context.rootGetters["UserModule/pharmacistInfo"];
      const currentUser = context.getters.user;
      const settings: Setting = { ...user.settings! };
      let userSaved: User;
      let savedSettings: Setting | null = null;
      let updatedUser = {
        ...currentUser
      };
      const metrcConfig = user.metrc || null;
      const biotrackConfig = user.biotrack || null;
      const hasMetrcConfig = !!user.hasMetrcConfig;
      const hasBiotrackConfig = !!user.hasBiotrackConfig;
      const formData: FormData = formParseUser(user);
      let pharmacist: PharmacistInterface = user.pharmacistData;
      pharmacist = setPharmacistLocations(pharmacist);
      const pharmacistFormData: FormData = formParsePharmacist(pharmacist);
      if (user.avatar && !user.avatar.name) {
        formData.set("avatar", user.avatar, "picture.png");
      } else if (user.avatar === null) {
        formData.set("avatar", DeleteCode.DELETE_CODE);
      }
      formData.append("_method", "PUT");
      // Update user
      const putResponse = await usersService.put(user, formData);
      if (getterPharmacistInfo && getterPharmacistInfo !== null) {
        const pharmacistPutResponse = await pharmacistService.put(pharmacist);
        context.commit("setPharmacistInfo", pharmacistPutResponse);
      } else {
        const pharmacistPostResponse = await pharmacistService.post(
          pharmacistFormData
        );

        context.commit("setPharmacistInfo", pharmacistPostResponse);
      }
      userSaved = putResponse.data;

      if (userSaved) {
        updatedUser = {
          ...updatedUser,
          ...userSaved
        };
      }

      if (
        userSaved.settings!.current_location_id !==
          settings.current_location_id ||
        userSaved.settings!.locale !== settings.locale
      ) {
        savedSettings = await usersService.updateSettings(userSaved.id!, {
          ...userSaved.settings!,
          ...settings
        });
      }

      if (savedSettings) {
        updatedUser = {
          ...updatedUser,
          settings: { ...savedSettings }
        };
      }

      if (userSaved && metrcConfig && !!+process.env.VUE_APP_METRC) {
        if (hasMetrcConfig) {
          if (
            !metrcConfig.user_key &&
            !metrcConfig.expiration &&
            !metrcConfig.occupational_license_number
          ) {
            traceabilityService.deleteUserTraceability(userSaved.id!);
          } else {
            traceabilityService.updateUserMetrcConfig(
              metrcConfig,
              userSaved.id!
            );
          }
        } else {
          traceabilityService.saveUserMetrcConfig(metrcConfig, userSaved.id!);
        }
      }

      if (userSaved && biotrackConfig) {
        if (hasBiotrackConfig) {
          if (!biotrackConfig.username && !biotrackConfig.password) {
            traceabilityService.deleteUserTraceability(userSaved.id!);
          } else {
            traceabilityService.updateUserBiotrackConfig(
              biotrackConfig,
              userSaved.id!
            );
          }
        } else {
          traceabilityService.saveUserBiotrackConfig(
            biotrackConfig,
            userSaved.id!
          );
        }
      }

      context.commit("setUser", updatedUser);

      EventBus.$emit("notify", { text: "user_saved", color: "success" });
      if (!user.routeCondition) {
        EventBus.$emit("route", {
          name: user.redirectTo || "dashboard"
        });
      }
    } catch (e) {
      messagesService.renderErrorMessage(e);
    } finally {
      context.commit("setLoading", false);
    }
  },
  registerApp(context: AuthActionContext) {
    const appMap = Cookie.get("app_map");
    const appId = `core_${Date.now()}`;
    context.commit("setAppId", appId);

    const map = appMap ? JSON.parse(appMap) : {};
    map[appId] = {
      idle: false
    };
    Cookie.set("app_map", JSON.stringify(map), {
      domain: `${process.env.VUE_APP_COOKIE_DOMAIN}`
    });
  },
  unregisterApp(context: AuthActionContext) {
    const appMap = Cookie.get("app_map");
    const appId: string = context.getters.appId;
    const map = JSON.parse(appMap!);
    Cookie.set("app_map", JSON.stringify(omit(map, [appId])), {
      domain: `${process.env.VUE_APP_COOKIE_DOMAIN}`
    });
  },
  setAppIdle(context: AuthActionContext, idleValue: boolean) {
    const appMap = Cookie.get("app_map");
    if (!appMap || appMap === "{}") {
      context.dispatch("logout");
      return;
    }
    const appId: string = context.getters.appId;
    const map = JSON.parse(appMap!);
    map[appId].idle = idleValue;
    Cookie.set("app_map", JSON.stringify(map), {
      domain: `${process.env.VUE_APP_COOKIE_DOMAIN}`
    });
  }
};
